import { Lock, LockOpen } from '@mui/icons-material';
import {
  FormControlLabel,
  IconButton,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useState } from 'react';
import { Controller, ControllerProps, useFormContext } from 'react-hook-form';

export interface InputProps extends Omit<ControllerProps, 'render'> {
  name: string;
  type?: string;
  label?: string;
  floatLabel?: boolean;
  placeholder?: string;
  disabled?: boolean;
  startAdornment?: JSX.Element;
  isLockable?: boolean;
  onChange?: TextFieldProps['onChange'];
  onBlur?: TextFieldProps['onBlur'];
  autoFocus?: boolean;
}

export function Input({
  name,
  type = 'text',
  label,
  floatLabel = false,
  placeholder,
  disabled = false,
  startAdornment,
  isLockable,
  onChange,
  onBlur,
  ...props
}: Readonly<InputProps>) {
  const methods = useFormContext();
  const [allowEditInput, setAllowEditInput] = useState<boolean>(false);
  return (
    <FormControlLabel
      labelPlacement="top"
      className="w-full m-0"
      label={
        !floatLabel && label ? (
          <Typography
            className={clsx(
              { 'text-mui-error': methods.formState.errors[name] },
              'ml-6 mb-1 font-bold'
            )}
            alignSelf="start"
          >
            {label}
            {props.rules?.required && !disabled && (
              <span className="ml-1 text-mui-error">*</span>
            )}
          </Typography>
        ) : null
      }
      control={
        <Controller
          {...props}
          rules={!disabled ? props.rules : undefined}
          name={name}
          render={({ field, fieldState: { error } }) => (
            <div className="w-full flex justify between gap-4">
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  if (onChange) onChange(e);
                }}
                onBlur={(e) => {
                  field.onBlur();
                  if (onBlur) onBlur(e);
                }}
                ref={field.ref}
                name={field.name}
                autoFocus={props.autoFocus}
                fullWidth
                size="small"
                label={floatLabel ? label : null}
                type={type}
                placeholder={placeholder}
                disabled={disabled || (isLockable && !allowEditInput)}
                error={!!error}
                helperText={error?.message}
                InputProps={{ startAdornment }}
              />
              {isLockable && (
                <IconButton
                  title={'Editar valor'}
                  color="secondary"
                  className={clsx(
                    'bg-orange-primary hover:bg-orange-primary',
                    'transition-all duration-200 hover:brightness-75',
                    'h-min w-min'
                  )}
                  onClick={() => {
                    setAllowEditInput(!allowEditInput);
                  }}
                >
                  {allowEditInput ? <LockOpen /> : <Lock />}
                </IconButton>
              )}
            </div>
          )}
        />
      }
    />
  );
}
